import type { CSSProperties } from 'vue'

// Deterministic per-PERSON avatar colour: a hash of the id picks one hue from
// the wheel below (his ruling 2026-08-09 — the hash-palette law, formerly
// sector-only, now includes people; the single navy token stays the
// no-identity fallback). Separate from lib/typeColors on purpose: nothing
// mirrors this server-side, so it can change freely.
// ★ A SOLID FILL WITH WHITE INITIALS (his call 2026-09-28: « always white text,
// do what is needed with the colour for it to be always beautiful »). This
// reverses the 09-24 « a tint, not a fill » ruling: once the job types became
// saturated discs, every pastel rung read as washed out beside them.
// ★ TWENTY HUES, EQUALLY BRIGHT (his QA 2026-09-28: « too much of the same
// colors », then « just nine colors?? »). A fixed Tailwind palette ran out of
// colours that stay distinct at the dark shade white text needs — the old eight
// held three pairs of twins. So the colour is COMPUTED in OKLCH: one lightness,
// twenty hues 18° apart. OKLCH's lightness is perceptual — in HSL a yellow
// looks far brighter than a blue at the « same » value — so every hue here
// carries white initials at the same contrast. 18° is about the smallest step
// that still reads as a different colour; this is the Fluent-style « ~two
// dozen hashed colours, all passing with white text », laid out perceptually
// instead of by hand.
// Same colour in both themes: a mid-dark disc sits well on either canvas.
const HUE_COUNT = 20
const HUE_STEP = 360 / HUE_COUNT
// Starts off the red axis so the first step is a rose, not a pure red.
const HUE_OFFSET = 12
// One lightness for every hue: that is what keeps white at the same contrast
// on all twenty (4.75:1 at the weakest).
const LIGHTNESS = 0.54
// ⚠ CHROMA IS PER HUE, and never one number for all. At L 0.54 the cyans and
// yellow-greens leave the sRGB gamut above ~0.09, while reds, blues and
// violets hold 0.15 — so a single chroma either clips (and a clipped colour
// loses the equal brightness) or mutes every hue down to the weakest one,
// which is the pastel this whole scheme was built to leave. Each value is its
// hue's sRGB limit at this lightness, capped at 0.15, rounded down; hue order
// follows the index (12°, 30°, 48° …). Change LIGHTNESS and this table goes
// with it — the spec re-derives both.
const CHROMA = [
  0.150, 0.150, 0.146, 0.119, 0.110, 0.112, 0.127, 0.150, 0.130, 0.102,
  0.092, 0.092, 0.104, 0.138, 0.150, 0.150, 0.150, 0.150, 0.150, 0.150,
] as const

// FNV-1a with a final avalanche: every bit depends on every character, and
// the order counts (djb2 taken mod a small count reduced to a character sum).
export const identityIndex = (id: string): number => {
  let hash = 0x811c9dc5
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  hash ^= hash >>> 16
  hash = Math.imul(hash, 0x85ebca6b)
  hash ^= hash >>> 13
  return (hash >>> 0) % HUE_COUNT
}

export const identityHue = (index: number): string =>
  `oklch(${LIGHTNESS} ${CHROMA[index]} ${(HUE_OFFSET + index * HUE_STEP) % 360})`

export type IdentityStyle = Pick<CSSProperties, 'backgroundColor' | 'color'>

// A disc with initials: the hue, and white on it.
export function avatarIdentityStyle(id?: string | null): IdentityStyle | null {
  return id ? { backgroundColor: identityHue(identityIndex(id)), color: '#ffffff' } : null
}

// ★ The same hue for MARKS too small to carry initials — a crew's 8 px dot on
// the planning month grid — so a crew's dot and its disc are one colour.
export function identityMarkStyle(id?: string | null): IdentityStyle | null {
  return id ? { backgroundColor: identityHue(identityIndex(id)) } : null
}

export { HUE_COUNT as IDENTITY_HUE_COUNT }
